"use client";

import * as React from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { Search, X } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getMessages } from "@/lib/messages";

/**
 * DESIGN.md §8, after Lifetime's locations page: search first, then filters as a scrollable chip
 * rail, then a plainly stated result count.
 *
 * 🔴 `areas` is passed in from the server and derived from the live gyms endpoint. v1 hardcoded
 * `["Salmiya", "Kuwait City", "Hawally", "Jabriya", "Fahaheel", "Farwaniya"]` — half of which
 * have no gym in them, so the filter advertised coverage the network does not have.
 */
export function GymFilters({
  areas,
  defaultQuery,
  defaultArea,
  defaultSort,
  defaultOpenNow,
}: {
  areas: string[];
  defaultQuery?: string;
  defaultArea?: string;
  defaultSort?: string;
  defaultOpenNow?: boolean;
}) {
  const messages = getMessages();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [query, setQuery] = React.useState(defaultQuery ?? "");

  function updateParam(key: string, value: string | null) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    router.push(`${pathname}?${params.toString()}`);
  }

  const activeCount =
    (defaultArea ? 1 : 0) + (defaultOpenNow ? 1 : 0) + (defaultQuery ? 1 : 0);

  const chip =
    "inline-flex h-11 shrink-0 items-center rounded-sm border px-4 text-caption transition-colors duration-150 ease-standard";
  const chipOff = "border-edge-control bg-surface text-ink-2 hover:border-ink-2 hover:text-ink";
  const chipOn = "border-signal bg-signal text-signal-fg";

  return (
    <div className="flex flex-col gap-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          updateParam("q", query || null);
        }}
        className="relative"
        role="search"
      >
        <label htmlFor="gym-search" className="sr-only">
          {messages.gyms.searchLabel}
        </label>
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <Input
          id="gym-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={messages.gyms.searchPlaceholder}
          className="h-12 pl-11"
        />
      </form>

      <div className="no-scrollbar flex items-center gap-2 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => updateParam("area", null)}
          className={cn(chip, defaultArea ? chipOff : chipOn)}
          aria-pressed={!defaultArea}
        >
          {messages.gyms.filterAllAreas}
        </button>

        {areas.map((area) => {
          const active = defaultArea === area;
          return (
            <button
              key={area}
              type="button"
              onClick={() => updateParam("area", active ? null : area)}
              className={cn(chip, active ? chipOn : chipOff)}
              aria-pressed={active}
            >
              {area}
            </button>
          );
        })}

        <span className="h-6 w-px shrink-0 bg-edge" aria-hidden="true" />

        <button
          type="button"
          onClick={() => updateParam("open_now", defaultOpenNow ? null : "true")}
          className={cn(chip, defaultOpenNow ? chipOn : chipOff)}
          aria-pressed={Boolean(defaultOpenNow)}
        >
          {messages.gyms.filterOpenNow}
        </button>

        <button
          type="button"
          onClick={() => updateParam("sort", defaultSort === "rating" ? null : "rating")}
          className={cn(chip, defaultSort === "rating" ? chipOn : chipOff)}
          aria-pressed={defaultSort === "rating"}
        >
          {messages.gyms.sortRating}
        </button>

        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              router.push(pathname);
            }}
            className={cn(chip, "gap-1.5 border-transparent text-signal hover:bg-signal-wash")}
          >
            <X className="h-4 w-4" aria-hidden="true" />
            {`Clear ${activeCount}`}
          </button>
        )}
      </div>
    </div>
  );
}
